/**
 * Twin of Containd `types/DefaultDropShadow.ts` for GenericItemHost paint.
 * Host cannot import this package; keep parse/resolve behavior aligned.
 */

export interface DefaultDropShadowSettings {
  enabled?: boolean;
  color?: string;
  opacity?: number;
  radius?: number;
  offsetX?: number;
  offsetY?: number;
  elevation?: number;
}

export const DEFAULT_DROP_SHADOW: Required<
  Pick<
    DefaultDropShadowSettings,
    'enabled' | 'opacity' | 'radius' | 'offsetX' | 'offsetY' | 'elevation'
  >
> = {
  enabled: true,
  opacity: 0.32,
  radius: 8,
  offsetX: 0,
  offsetY: 4,
  elevation: 6,
};

export const DEFAULT_DROP_SHADOW_COLOR_FALLBACK = '#000000';

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function readOptionalNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return value;
}

function readOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value !== 'boolean') {
    return undefined;
  }
  return value;
}

function readOptionalColor(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) {
    return undefined;
  }
  return trimmed;
}

function normalizePartial(raw: Record<string, unknown>): DefaultDropShadowSettings {
  const result: DefaultDropShadowSettings = {};

  const enabled = readOptionalBoolean(raw.enabled);
  if (enabled !== undefined) result.enabled = enabled;

  const color = readOptionalColor(raw.color);
  if (color !== undefined) result.color = color;

  const opacity = readOptionalNumber(raw.opacity);
  if (opacity !== undefined) result.opacity = clamp01(opacity);

  const radius = readOptionalNumber(raw.radius);
  if (radius !== undefined && radius >= 0) result.radius = radius;

  const offsetX = readOptionalNumber(raw.offsetX);
  if (offsetX !== undefined) result.offsetX = offsetX;

  const offsetY = readOptionalNumber(raw.offsetY);
  if (offsetY !== undefined) result.offsetY = offsetY;

  const elevation = readOptionalNumber(raw.elevation);
  if (elevation !== undefined && elevation >= 0) {
    result.elevation = Math.floor(elevation);
  }

  return result;
}

export function parseDefaultDropShadow(raw?: string | null): DefaultDropShadowSettings | null {
  if (raw == null || raw.trim().length === 0) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed == null || Array.isArray(parsed)) {
    return null;
  }

  const normalized = normalizePartial(parsed as Record<string, unknown>);
  return Object.keys(normalized).length > 0 ? normalized : null;
}

export interface ResolvedDefaultDropShadow {
  enabled: boolean;
  color: string;
  opacity: number;
  radius: number;
  offsetX: number;
  offsetY: number;
  elevation: number;
}

export function resolveDefaultDropShadow(
  raw?: string | null,
  options?: { fallbackColor?: string },
): ResolvedDefaultDropShadow {
  const parsed = parseDefaultDropShadow(raw);
  const fallbackColor =
    options?.fallbackColor?.trim() || DEFAULT_DROP_SHADOW_COLOR_FALLBACK;
  const enabled = parsed != null && parsed.enabled !== false;

  return {
    enabled,
    color: parsed?.color ?? fallbackColor,
    opacity: parsed?.opacity ?? DEFAULT_DROP_SHADOW.opacity,
    radius: parsed?.radius ?? DEFAULT_DROP_SHADOW.radius,
    offsetX: parsed?.offsetX ?? DEFAULT_DROP_SHADOW.offsetX,
    offsetY: parsed?.offsetY ?? DEFAULT_DROP_SHADOW.offsetY,
    elevation: parsed?.elevation ?? DEFAULT_DROP_SHADOW.elevation,
  };
}

/** CSS box-shadow from resolved settings (empty when disabled). */
export function dropShadowToBoxShadow(shadow: ResolvedDefaultDropShadow): string | undefined {
  if (!shadow.enabled) {
    return undefined;
  }
  const color = shadow.color;
  // Prefer rgba when hex includes alpha; otherwise append opacity via color-mix/fallback.
  if (/^#[0-9a-fA-F]{8}$/.test(color)) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const a = (parseInt(color.slice(7, 9), 16) / 255) * shadow.opacity;
    return `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.radius}px rgba(${r},${g},${b},${a})`;
  }
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.radius}px rgba(${r},${g},${b},${shadow.opacity})`;
  }
  return `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.radius}px ${color}`;
}
